import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';

/**
 * A secret, shown exactly once — the shape `Pages/Fleet/components/BridgeDialogs.tsx`'s
 * `BridgeTokenDialog` established for a print-bridge token, reused here for a
 * webhook signing secret because the guarantee is identical: nothing behind
 * this app can ever read it back. ADR-0004 is explicit that
 * `POST /webhooks/endpoints` and `POST .../rotate-secret` are the only two
 * moments in the secret's life it is ever returned, and every later read goes
 * through `ENDPOINT_PUBLIC_SELECT`, which does not name the column at all —
 * so there is deliberately no "reveal" affordance anywhere else in this
 * feature, because there is nothing behind one.
 *
 * `dir="ltr"` on the secret itself, unconditionally — a secret is a base64url
 * / hex string, and in an RTL reading direction a mixed run like that can
 * mirror or reorder visually (digits and Latin runs are LTR "islands" inside
 * bidi text; where exactly they land depends on what surrounds them). A
 * character transcribed wrong from a mirrored secret fails silently at the
 * call site with no signal pointing back at this dialog, so the direction is
 * pinned rather than left to the document's.
 */
export function SecretRevealDialog({
  open,
  secret,
  title,
  intro,
  onClose,
}: {
  open: boolean;
  secret: string | null;
  title: string;
  intro?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const copy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      toast.success(t('integrations.secret.copied'));
    } catch {
      toast.error(t('integrations.secret.copyFailed'));
    }
  };

  return (
    <Dialog
      open={open && secret !== null}
      onClose={onClose}
      title={title}
      description={intro}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {secret && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
            <Icon as={TriangleAlert} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p>{t('integrations.secret.shownOnce')}</p>
          </div>
          <div className="rounded-lg border bg-subtle p-3">
            <code dir="ltr" className="block break-all font-mono text-sm">
              {secret}
            </code>
          </div>
          <Button variant="outline" onClick={() => void copy()}>
            <Icon as={Copy} />
            {t('integrations.secret.copy')}
          </Button>
        </div>
      )}
    </Dialog>
  );
}

export default SecretRevealDialog;
